import { useEffect, useState } from "react";
import styles from "./CallDetails.module.css";

const CallDetails = ({ callerId, campaignId }) => {
  const [activeTab, setActiveTab] = useState("transcript");
  const [transcriptData, setTranscriptData] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [leadAnalysis, setLeadAnalysis] = useState(null); // New state for lead analysis
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [loading, setLoading] = useState({
    transcript: false,
    userInfo: false,
    leadAnalysis: false,
    recording: false,
  });
  const [error, setError] = useState({
    transcript: null,
    userInfo: null,
    leadAnalysis: null,
    recording: null,
  });

  const authData = JSON.parse(localStorage.getItem("authData") || "{}");
  const token = authData?.token;
  const userId = authData?.user?.id;
  const vendorId = authData?.user?.vendorId;

  const BASE_API_URL =
    "https://prod.toxboxai.com/api/call-history/files";
  const S3_BASE_URL = "https://toxbox-images.s3.ap-south-1.amazonaws.com";

  useEffect(() => {
    const fetchCallData = async () => {
      if (!token || !userId || !vendorId || !campaignId || !callerId) {
        setError({
          transcript: "Missing authentication or required parameters",
          userInfo: "Missing authentication or required parameters",
          leadAnalysis: "Missing authentication or required parameters",
          recording: "Missing authentication or required parameters",
        });
        return;
      }

      try {
        setLoading({
          transcript: true,
          userInfo: true,
          leadAnalysis: true,
          recording: true,
        });

        // Fetch file list
        console.log(
          "Fetching file list from:",
          `${BASE_API_URL}/${campaignId}/${callerId}`
        );
        const filesResponse = await fetch(
          `${BASE_API_URL}/${campaignId}/${callerId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!filesResponse.ok) {
          throw new Error(
            `Failed to fetch file list: ${filesResponse.statusText}`
          );
        }
        const { files } = await filesResponse.json();
        console.log("Files received:", files);

        // Filter files for the specific callerId
        const callerFiles = files.filter((file) => file.includes(callerId));
        console.log("Filtered files for callerId:", callerFiles);

        // Define file names to look for
        const statusFile = callerFiles.find((file) =>
          file.endsWith("call_status.json")
        );
        const recordingFile = callerFiles.find((file) =>
          file.endsWith("recording.wav")
        );
        const transcriptFile = callerFiles.find((file) =>
          file.endsWith("transcript.txt")
        );
        const leadAnalysisFile = callerFiles.find((file) =>
          file.endsWith("lead_analysis.json")
        );

        // Fetch call status
        if (statusFile) {
          try {
            const statusResponse = await fetch(`${S3_BASE_URL}/${statusFile}`);
            if (!statusResponse.ok) {
              throw new Error("Failed to fetch call status");
            }
            const statusData = await statusResponse.json();
            setUserInfo(statusData);
          } catch (err) {
            setError((prev) => ({ ...prev, userInfo: err.message }));
          }
        } else {
          setError((prev) => ({
            ...prev,
            userInfo: "No call status file found",
          }));
        }

        // Fetch lead analysis
        if (leadAnalysisFile) {
          try {
            const leadAnalysisResponse = await fetch(
              `${S3_BASE_URL}/${leadAnalysisFile}`
            );
            if (!leadAnalysisResponse.ok) {
              throw new Error("Failed to fetch lead analysis");
            }
            const leadAnalysisData = await leadAnalysisResponse.json();
            setLeadAnalysis(leadAnalysisData);
          } catch (err) {
            setError((prev) => ({ ...prev, leadAnalysis: err.message }));
          }
        } else {
          setError((prev) => ({
            ...prev,
            leadAnalysis: "No lead analysis file found",
          }));
        }

        // Fetch transcript
        if (transcriptFile) {
          try {
            const transcriptResponse = await fetch(
              `${S3_BASE_URL}/${transcriptFile}`
            );
            if (!transcriptResponse.ok) {
              throw new Error("Failed to fetch transcript");
            }
            const transcriptText = await transcriptResponse.text();
            const parsedTranscript = parseTranscript(transcriptText);
            setTranscriptData(parsedTranscript);
          } catch (err) {
            setError((prev) => ({ ...prev, transcript: err.message }));
          }
        } else {
          setError((prev) => ({
            ...prev,
            transcript: "No transcript file found",
          }));
        }

        // Fetch recording
        if (recordingFile) {
          const url = `${S3_BASE_URL}/${recordingFile}?t=${Date.now()}`;
          console.log("Setting recording URL:", url);
          setRecordingUrl(url);
        } else {
          setError((prev) => ({
            ...prev,
            recording: "No recording file found",
          }));
        }
      } catch (err) {
        console.error("Error fetching call data:", err);
        setError({
          transcript: err.message,
          userInfo: err.message,
          leadAnalysis: err.message,
          recording: err.message,
        });
      } finally {
        setLoading({
          transcript: false,
          userInfo: false,
          leadAnalysis: false,
          recording: false,
        });
      }
    };

    fetchCallData();
  }, [campaignId, callerId, vendorId, token, userId]);

  const parseTranscript = (text) => {
    if (!text || typeof text !== "string") return [];
    const lines = text.split("\n").filter((line) => line.trim());
    return lines.map((line) => {
      const match = line.match(/^(\w+):\s*(.*)$/);
      return match
        ? { speaker: match[1].trim(), text: match[2].trim() }
        : { speaker: "Unknown", text: line.trim() };
    });
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Call Details</h2>

      {/* Display Lead Analysis Summary */}
      <h3>Lead Summary</h3>
      {loading.leadAnalysis && (
        <div className={styles.loading}>Loading lead analysis...</div>
      )}
      {error.leadAnalysis && (
        <div className={styles.error}>Error: {error.leadAnalysis}</div>
      )}
      {!loading.leadAnalysis && !error.leadAnalysis && leadAnalysis ? (
        <div className={styles.leadAnalysis}>
          <p>
            <strong>Lead Type:</strong> {leadAnalysis.leadType || "N/A"}
          </p>
          <p>
            <strong>Summary:</strong>{" "}
            {leadAnalysis.summary || "No summary available"}
          </p>
          <p>
            <strong>Rating:</strong> {leadAnalysis.rating || "N/A"}
          </p>
        </div>
      ) : (
        !loading.leadAnalysis &&
        !error.leadAnalysis && <div>No lead analysis available</div>
      )}

      <div className={styles.tabContainer}>
        <button
          className={`${styles.tab} ${activeTab === "transcript" ? styles.activeTab : ""
            }`}
          onClick={() => setActiveTab("transcript")}
          aria-selected={activeTab === "transcript"}
          role="tab"
        >
          Transcript
        </button>
        <button
          className={`${styles.tab} ${activeTab === "userInfo" ? styles.activeTab : ""
            }`}
          onClick={() => setActiveTab("userInfo")}
          aria-selected={activeTab === "userInfo"}
          role="tab"
        >
          User Detail
        </button>
        <button
          className={`${styles.tab} ${activeTab === "recording" ? styles.activeTab : ""
            }`}
          onClick={() => setActiveTab("recording")}
          aria-selected={activeTab === "recording"}
          role="tab"
        >
          Call Recording
        </button>
      </div>

      <div className={styles.content}>
        {activeTab === "transcript" && (
          <div className={styles.transcript}>
            {loading.transcript && (
              <div className={styles.loading}>Loading transcript...</div>
            )}
            {error.transcript && (
              <div className={styles.error}>Error: {error.transcript}</div>
            )}
            {!loading.transcript &&
              !error.transcript &&
              (transcriptData.length > 0 ? (
                transcriptData.map((entry, index) => (
                  <div
                    key={index}
                    className={`${styles.message} ${entry.speaker === "Agent"
                      ? styles.agentMessage
                      : styles.userMessage
                      }`}
                  >
                    <span className={styles.speaker}>{entry.speaker}:</span>
                    <span className={styles.messageText}>{entry.text}</span>
                  </div>
                ))
              ) : (
                <div>No transcript available</div>
              ))}
          </div>
        )}

        {activeTab === "userInfo" && (
          <div className={styles.userInfo}>
            {loading.userInfo && (
              <div className={styles.loading}>Loading user info...</div>
            )}
            {error.userInfo && (
              <div className={styles.error}>Error: {error.userInfo}</div>
            )}
            {!loading.userInfo && !error.userInfo && userInfo && (
              <table className={styles.infoTable}>
                <thead>
                  <tr>
                    <th scope="col">Key</th>
                    <th scope="col">Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Call ID</td>
                    <td>{userInfo.callId || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>Caller ID</td>
                    <td>{callerId}</td>
                  </tr>
                  <tr>
                    <td>Vendor ID</td>
                    <td>{vendorId}</td>
                  </tr>
                  <tr>
                    <td>Campaign ID</td>
                    <td>{campaignId}</td>
                  </tr>
                  <tr>
                    <td>User ID</td>
                    <td>{userId}</td>
                  </tr>
                  <tr>
                    <td>Client Version</td>
                    <td>{userInfo.clientVersion || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>Created</td>
                    <td>
                      {userInfo.created
                        ? new Date(userInfo.created).toLocaleString()
                        : "N/A"}
                    </td>
                  </tr>
                  <tr>
                    <td>Joined</td>
                    <td>
                      {userInfo.joined
                        ? new Date(userInfo.joined).toLocaleString()
                        : "N/A"}
                    </td>
                  </tr>
                  <tr>
                    <td>Ended</td>
                    <td>
                      {userInfo.ended
                        ? new Date(userInfo.ended).toLocaleString()
                        : "N/A"}
                    </td>
                  </tr>
                  <tr>
                    <td>End Reason</td>
                    <td>{userInfo.endReason || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>Billed Duration</td>
                    <td>{userInfo.billedDuration || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>First Speaker</td>
                    <td>{userInfo.firstSpeaker || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>Language Hint</td>
                    <td>{userInfo.languageHint || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>Max Duration</td>
                    <td>{userInfo.maxDuration || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>Model</td>
                    <td>{userInfo.model || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>Recording Enabled</td>
                    <td>{userInfo.recordingEnabled ? "Yes" : "No"}</td>
                  </tr>
                  <tr>
                    <td>System Prompt</td>
                    <td>{userInfo.systemPrompt || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>Temperature</td>
                    <td>{userInfo.temperature || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>Voice ID</td>
                    <td>{userInfo.voice || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>Short Summary</td>
                    <td>{userInfo.shortSummary || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>Inactivity Message</td>
                    <td>
                      {userInfo.inactivityMessages?.[0]?.message || "N/A"}
                    </td>
                  </tr>
                  <tr>
                    <td>Inactivity Duration</td>
                    <td>
                      {userInfo.inactivityMessages?.[0]?.duration || "N/A"}
                    </td>
                  </tr>
                  <tr>
                    <td>Initial Output Medium</td>
                    <td>{userInfo.initialOutputMedium || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>Join Timeout</td>
                    <td>{userInfo.joinTimeout || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>Time Exceeded Message</td>
                    <td>{userInfo.timeExceededMessage || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>Error Count</td>
                    <td>{userInfo.errorCount || "N/A"}</td>
                  </tr>
                  <tr>
                    <td>Plivo Call ID</td>
                    <td>
                      {userInfo.metadata?.["ultravox.plivo.call_id"] || "N/A"}
                    </td>
                  </tr>
                  <tr>
                    <td>Plivo Stream ID</td>
                    <td>
                      {userInfo.metadata?.["ultravox.plivo.stream_id"] || "N/A"}
                    </td>
                  </tr>
                  <tr>
                    <td>Plivo Account ID</td>
                    <td>
                      {userInfo.metadata?.["ultravox.plivo.account_id"] ||
                        "N/A"}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
            {!loading.userInfo && !error.userInfo && !userInfo && (
              <div>No user information available</div>
            )}
          </div>
        )}

        {activeTab === "recording" && (
          <div className={styles.recording}>
            <h3 className={styles.recordingTitle}>Call Recording</h3>
            {loading.recording && (
              <div className={styles.loading}>Loading recording...</div>
            )}
            {error.recording && (
              <div className={styles.error}>Error: {error.recording}</div>
            )}
            {!loading.recording && !error.recording && recordingUrl ? (
              <audio
                controls
                className={styles.audioPlayer}
                preload="metadata"
                aria-label="Call recording playback"
                onError={(e) => {
                  console.error("Audio error:", e.nativeEvent);
                  setError((prev) => ({
                    ...prev,
                    recording: "Failed to load audio file",
                  }));
                }}
              >
                <source src={recordingUrl} type="audio/wav" />
                Your browser does not support the audio element.
              </audio>
            ) : (
              !loading.recording &&
              !error.recording && <div>No recording available</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CallDetails;
